import { Injectable } from '@angular/core';
import { PDFDocument, PDFName } from 'pdf-lib';
import { PdfFieldInfo } from '../models/pdf-field-info.model';

@Injectable({
  providedIn: 'root'
})
export class PdfFieldExtractorService {

  async extractFields(bytes: ArrayBuffer): Promise<{ pdfDoc: PDFDocument; fields: PdfFieldInfo[] }> {
    const pdfDoc = await PDFDocument.load(bytes);
    const form = pdfDoc.getForm();
    const fields = form.getFields();
    
    const fieldsInfo: PdfFieldInfo[] = [];
    for (const field of fields) {
      const name = field.getName();
      const widgets = field.acroField.getWidgets();
      const type = field.constructor.name === 'PDFCheckBox' ? 'checkbox' : 'text';

      for (const widget of widgets) {
        // Find which page this widget is on
        let pageIndex = -1;
        const dict = widget.dict;
        
        // PDF-lib provides a reference to the page the widget is on
        // Sometimes it's direct via P
        const pRef = dict.get(PDFName.of('P'));
        if (pRef) {
          const pages = pdfDoc.getPages();
          pageIndex = pages.findIndex(p => p.ref === pRef);
        }

        // Fallback: If pageIndex is still -1, try to locate it through annotations
        if (pageIndex === -1) {
          const pages = pdfDoc.getPages();
          for (let i = 0; i < pages.length; i++) {
            const annots = pages[i].node.Annots();
            if (annots) {
              const widgetRef = pdfDoc.context.getObjectRef(widget.dict);
              // Check if widgetRef is in the annotations array
              const annotsArray = annots.asArray();
              if (annotsArray.some(ref => ref === widgetRef)) {
                pageIndex = i;
                break;
              }
            }
          }
        }

        const rect = widget.getRectangle();
        let widgetType = type;

        if (rect && rect.width < 25 && rect.height < 25) {
          widgetType = 'checkbox';
        }

        if (rect && pageIndex !== -1) {
          fieldsInfo.push({
            name,
            pageIndex,
            rect,
            type: widgetType
          });
        }
      }
    }

    return { pdfDoc, fields: fieldsInfo };
  }

  public rectToNumbers(rect: any): { x: number; y: number; width: number; height: number } | null {
    if (!rect) return null;
    
    // Already normalized
    if (typeof rect.x === 'number' && typeof rect.y === 'number' && typeof rect.width === 'number' && typeof rect.height === 'number') {
      return rect;
    }

    let x1: any, y1: any, x2: any, y2: any;

    if (rect.constructor && rect.constructor.name === 'PDFArray') {
      const arr = rect.asArray();
      if (arr.length >= 4) {
        x1 = arr[0];
        y1 = arr[1];
        x2 = arr[2];
        y2 = arr[3];
      } else {
        return null;
      }
    } else if (Array.isArray(rect) && rect.length >= 4) {
      x1 = rect[0];
      y1 = rect[1];
      x2 = rect[2];
      y2 = rect[3];
    } else {
      return null;
    }

    // Handle PDFNumber wrappers if present
    x1 = typeof x1?.asNumber === 'function' ? x1.asNumber() : x1;
    y1 = typeof y1?.asNumber === 'function' ? y1.asNumber() : y1;
    x2 = typeof x2?.asNumber === 'function' ? x2.asNumber() : x2;
    y2 = typeof y2?.asNumber === 'function' ? y2.asNumber() : y2;

    if (typeof x1 !== 'number' || typeof y1 !== 'number' || typeof x2 !== 'number' || typeof y2 !== 'number') {
      return null;
    }

    return {
      x: Math.min(x1, x2),
      y: Math.min(y1, y2),
      width: Math.abs(x2 - x1),
      height: Math.abs(y2 - y1)
    };
  }
}
